/**
 * Lifting the phone app's launch splash.
 *
 * The OS splash (@capacitor/splash-screen) is configured with `launchAutoHide: false` in
 * capacitor.config.ts: it stays on the logo until the web app has mounted its own LaunchSplash,
 * which is the same picture, and only then is it hidden. While it is up the WebView draws nothing
 * at all, so the first frame it draws on hide is LaunchSplash's. The person sees one splash, not
 * the logo, a blank WebView, then the logo again. index.html's inline script (and MainActivity,
 * where the native project has it) hides it after 8s regardless, in case the bundle never gets
 * this far.
 *
 * On the web this does nothing (there is no OS splash), and the plugin is imported only in the phone
 * app so the website's bundle does not load it at start. It never throws: a splash that would not
 * hide is not a reason to break the screen behind it, and the native backstop still lifts it.
 */

import { Capacitor, registerPlugin, SystemBars, SystemBarType, SystemBarsStyle } from '@capacitor/core';

/** The fade on hide, in ms. At launch on Android 12+ the config's launchFadeOutDuration (the same
 *  250) is what applies; this covers a splash shown again later with SplashScreen.show(). */
const FADE_OUT_MS = 250;

export async function hideNativeSplash(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide({ fadeOutDuration: FADE_OUT_MS });
  } catch {
    // Plugin missing from this build, or the native call failed: the 8s backstops hide it.
  }
}

/**
 * Launch-blue system bars with light icons while a brand screen is up, then back to the theme's own.
 * The page stays inside the bars (no edge-to-edge window), so on Android 14 and older the bars are
 * painted blue natively (BrandBarsPlugin) or the blue splash sits between two white bands; the
 * default light-theme icons are dark, unreadable on that blue.
 *
 * A screen claims only the bars it paints under: the launch splash is full-bleed blue, so it claims
 * both (`'all'`); the first-run intro has a blue hero at the top but a plain white footer under the
 * navigation bar, so it claims only the status bar (`'status'`) and the gesture handle / 3-button
 * icons stay dark on white.
 *
 * Counted per bar rather than set, because the two overlap: the intro mounts under the splash and
 * claims before the splash lets go, so the splash's release must not hand the intro a dark status
 * bar, but must still hand the navigation bar back to the theme. Returns the release, safe to call
 * more than once (an effect cleanup). A no-op on the web.
 */
export type BrandBars = 'all' | 'status';

const brandClaims: Record<SystemBarType, number> = {
  [SystemBarType.StatusBar]: 0,
  [SystemBarType.NavigationBar]: 0,
};

export function claimOnBrandSystemBars(bars: BrandBars = 'all'): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};
  const claimed =
    bars === 'all' ? [SystemBarType.StatusBar, SystemBarType.NavigationBar] : [SystemBarType.StatusBar];
  for (const bar of claimed) {
    brandClaims[bar] += 1;
    if (brandClaims[bar] === 1) void applySystemBarsStyle(bar, true);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    for (const bar of claimed) {
      brandClaims[bar] -= 1;
      if (brandClaims[bar] === 0) void applySystemBarsStyle(bar, false);
    }
  };
}

/** Test seam: forget outstanding claims between tests. */
export function resetSystemBarsClaimsForTests() {
  brandClaims[SystemBarType.StatusBar] = 0;
  brandClaims[SystemBarType.NavigationBar] = 0;
}

/** native-android/BrandBarsPlugin.java: the bar's own colour, launch blue or the theme's (Android 14 and older). */
interface BrandBarsPlugin {
  paint(options: { bar: 'status' | 'navigation'; onBrand: boolean }): Promise<void>;
}
let brandBars: BrandBarsPlugin | null = null;

function applySystemBarsStyle(bar: SystemBarType, onBrand: boolean): Promise<unknown> {
  return Promise.all([paintBar(bar, onBrand), styleIcons(bar, onBrand)]);
}

async function paintBar(bar: SystemBarType, onBrand: boolean): Promise<void> {
  try {
    brandBars ??= registerPlugin<BrandBarsPlugin>('BrandBars');
    await brandBars.paint({ bar: bar === SystemBarType.StatusBar ? 'status' : 'navigation', onBrand });
  } catch {
    // An older native shell without the plugin: the bar keeps the theme's colour.
  }
}

async function styleIcons(bar: SystemBarType, onBrand: boolean): Promise<void> {
  try {
    // DARK is Capacitor's name for "light icons, for a dark background".
    await SystemBars.setStyle({ style: onBrand ? SystemBarsStyle.Dark : SystemBarsStyle.Default, bar });
  } catch {
    // An older native shell without SystemBars: the icons keep the theme's colour, nothing breaks.
  }
}
